/* eslint-disable new-cap */
import api from '@/api';
import { getQueryParam } from '@/api/helper';
import { API_ENDPOINTS, SELFIE_KYC_ERRORS } from '@/constants';
import {
  GetESignApiErrorResponse,
  GetESignApiSuccessResponse,
  GetESignResponse,
} from '@/api/types';

interface SelfieKycSuccessResponse {
  success: boolean;
  next?: {
    method: string;
    url: string;
  };
}

interface SelfieKycErrorResponse {
  code: number;
  message: string;
  details?: Array<{
    code: string;
  }>;
}

const ErrorMap = {
  PGCE000042: SELFIE_KYC_ERRORS.KYC_FAILED,
  PGCE000043: SELFIE_KYC_ERRORS.MAX_RETRY_LIMIT_REACHED,
};

interface DoSelfieKYCResponse {
  success: boolean;
  url?: string;
  error?: (typeof SELFIE_KYC_ERRORS)[keyof typeof SELFIE_KYC_ERRORS];
}

/**
 *
 * The doSelfieKYC function is used to perform the selfie KYC for the user.
 * @param paymentId The payment id of the transaction
 * @param image Image in base64 format
 */
export const doSelfieKYC = async (image: string): Promise<DoSelfieKYCResponse> => {
  const paymentId = getQueryParam('id') as string;
  const response = await api(
    API_ENDPOINTS.KYC_CHECK(paymentId),
    {},
    {
      method: 'POST',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      body: JSON.stringify({ face_image: image }),
    },
  );

  if (response.ok) {
    const data = (await response.json()) as SelfieKycSuccessResponse;
    return { success: true, url: data.next?.url };
  } else {
    const errorData = (await response.json()) as SelfieKycErrorResponse;

    const knownError = errorData.details?.find((detail) =>
      Object.keys(ErrorMap).includes(detail.code),
    );

    if (knownError) {
      return { success: false, error: ErrorMap[knownError.code as keyof typeof ErrorMap] };
    }

    throw new Error('Something went wrong.');
  }
};

export const getESignUrl = async (): Promise<GetESignResponse> => {
  const paymentId = getQueryParam('id') as string;
  const response = await api(API_ENDPOINTS.E_SIGN(paymentId), {}, { method: 'GET' });

  if (response.ok) {
    const data = (await response.json()) as GetESignApiSuccessResponse;
    return { success: true, url: data.next?.url };
  } else {
    const errorData = (await response.json()) as GetESignApiErrorResponse;
    return {
      success: false,
      errors: errorData.payload?.details?.map(({ code, description }) => ({ code, description })),
    };
  }
};

export const getEmandateUrl = async (): Promise<GetESignResponse> => {
  const paymentId = getQueryParam('id') as string;
  const response = await api(API_ENDPOINTS.EMANDATE(paymentId), {}, { method: 'GET' });

  if (response.ok) {
    const data = (await response.json()) as GetESignApiSuccessResponse;
    return { success: true, url: data.next?.url };
  } else {
    const errorData = (await response.json()) as GetESignApiErrorResponse;
    return {
      success: false,
      errors: errorData.payload?.details?.map(({ code, description }) => ({ code, description })),
    };
  }
};

export const getEmandateStatus = async (pgPaymentId: string): Promise<GetESignResponse> => {
  const paymentId = getQueryParam('id') as string;
  const response = await api(
    API_ENDPOINTS.EMANDATE_STATUS(paymentId),
    {},
    {
      method: 'POST',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      body: JSON.stringify({ repayment_id: pgPaymentId }),
    },
  );

  if (response.ok) {
    const data = (await response.json()) as GetESignResponse;
    return { success: data.success };
  } else {
    const errorData = (await response.json()) as GetESignApiErrorResponse;
    return {
      success: false,
      errors: errorData.payload?.details?.map(({ code, description }) => ({ code, description })),
    };
  }
};
